import React, { useEffect, useState } from 'react'

import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { ColorPicker } from 'plugin-codaf-risk-ext-bowtie/generic'
import { IBarrierStatusDTO, IBarrierStatusSpecialStatus } from 'plugin-codaf-risk-ext-bowtie/types'
import { sortNumber } from 'plugin-codaf-risk-ext-bowtie/utils'

import { getBarrierStatus, updateOrCreateBarrierStatus } from '../../../services'
import { CUSTOM_STATUS_NAME_MAX_CHARS } from '../../../utils/constants'
import { BarrierStatusNameStyled } from './styles'

export const BarrierStatusName: React.FC = () => {
    const [editMode, setEditMode] = useState(false)
    const [barrierStatus, setBarrierStatus] = useState<IBarrierStatusSpecialStatus[]>()
    const [inputFocus, setInputFocus] = useState<boolean[]>(new Array(barrierStatus?.length))

    const priorityOrderSortFunction = (s1, s2) => s1.priorityOrder - s2.priorityOrder

    const handleGetBarrierStatus = async (): Promise<void> => {
        try {
            const data = await getBarrierStatus()
            data.sort(priorityOrderSortFunction)
            const initialBooleanArray = data.map((bs: IBarrierStatusSpecialStatus) => {
                return false
            })
            setInputFocus(initialBooleanArray)
            setBarrierStatus(null)
            setBarrierStatus(data)

        } catch (err) {
            globalNotification.error(err.message ?? i18n('An error occurred on get barrier status.'))
        }
    }

    const handleSaveBarrierStatus = async (): Promise<void> => {
        try {
            const barrierStatusObject: IBarrierStatusDTO = { barrierStatusDtoList: barrierStatus }
            await updateOrCreateBarrierStatus(barrierStatusObject)
            await handleGetBarrierStatus()
            // const sortedBarrierStatus = data?.sort(priorityOrderSortFunction)
            // setBarrierStatus(sortedBarrierStatus)
            globalNotification.success(i18n('Barrier status custom names updated successfully.'))
            setEditMode(false)
        } catch (err) {
            globalNotification.error(err.message ?? i18n('An error occurred when trying to save barrier status.'))
            console.log(err)
        }
    }

    const generatePriorityOrderNumber = (
        position: 'TOP' | 'BOT',
        statusList: IBarrierStatusSpecialStatus[],
        mapIndex: number
    ): number => {
        if (position === 'TOP') {
            return Math.floor((statusList[mapIndex].priorityOrder + statusList[mapIndex - 1].priorityOrder) / 2)
        } else {
            return Math.floor((statusList[mapIndex].priorityOrder + statusList[mapIndex + 1].priorityOrder) / 2)
        }
    }

    const handleNewStatus = (
        position: 'TOP' | 'BOT',
        statusList: IBarrierStatusSpecialStatus[],
        mapIndex: number
    ): void => {
        const updatedBooleanArray = inputFocus.map(boo => {
            return boo
        })
        if (position === 'TOP') {
            updatedBooleanArray.unshift(false)
        } else {
            updatedBooleanArray.push(false)
        }
        setInputFocus(updatedBooleanArray)
        setBarrierStatus([
            ...barrierStatus,
            {
                colorReference: '#dadada',
                deleteable: true,
                priorityOrder: generatePriorityOrderNumber(position, statusList, mapIndex),
                statusCustomName: ''
            }
        ])
    }

    const handleRemoveStatus = (index: number): void => {
        const statusArray: IBarrierStatusSpecialStatus[] = barrierStatus.map(bs => {
            return bs
        })
        statusArray.splice(index, 1)
        setBarrierStatus(statusArray)
    }

    const handleCancel = (): void => {
        setEditMode(false)
        handleGetBarrierStatus()
    }

    const isNullOrEmptyString = (name: string): boolean => {
        return name == null || name === ''
    }

    useEffect(() => {
        handleGetBarrierStatus()
    }, [])

    return (
        <BarrierStatusNameStyled>
            <div className="barrier-status-name-title ">{i18n('Barrier Status Nomenclature Change')}</div>

            <div className="barrier-status-name-subtitle ">
                {i18n(
                    'The system allows you to change the default nomenclature used for barrier status. Default colors cannot be changed.'
                )}
            </div>
            <div className="barrier-status-name-subtitle ">
                <div className="barrier-status-attention">{i18n('WARNING: ')}</div>
                <div>{i18n('Changes on the default nomenclature will affect all barriers in the system.')}</div>
            </div>

            <div className="barrier-status-container ">
                <div className={` barrier-status-header ${editMode ? 'editing' : ''}`}>
                    <div className={`barrier-status-header-tag color  ${editMode ? 'color-editing' : ''}`}>
                        {i18n('Color')}
                    </div>
                    <div className={`barrier-status-header-tag name ${editMode ? 'name-editing' : ''}`}>
                        {i18n('Nomenclature')}
                    </div>
                    <div className={`barrier-status-edit-icon ${editMode ? 'editing' : ''}`}>
                        <i className="far fa-edit" onClick={() => setEditMode(true)} />
                    </div>
                </div>
                <div className="barrier-status-content">
                    {barrierStatus
                        ?.sort((s1, s2) => s1.priorityOrder - s2.priorityOrder)
                        .map((el, index) => (
                            <div key={index} className="barrier-status-item-wrapper">
                                {barrierStatus.length < 11 &&
                                    editMode &&
                                    el.priorityOrder !== -1000 &&
                                    el.priorityOrder !== 2000 && (
                                        <div className={`new-status-row ${inputFocus[index] === true ? 'focus' : ''}`}>
                                            <div className="new-status-line"></div>
                                            <div
                                                className="new-status-button"
                                                onClick={() => {
                                                    handleNewStatus('TOP', barrierStatus.sort(sortNumber), index)
                                                }}
                                            >
                                                <i className="fas fa-plus" />
                                                {i18n('New Status')}
                                            </div>
                                            <div className="new-status-line"></div>
                                        </div>
                                    )}
                                <div className="barrier-status">
                                    {editMode ? (
                                        <>
                                            {el.specialStatus ? (
                                                <div
                                                    className="color-box"
                                                    style={{ backgroundColor: el.colorReference }}
                                                />
                                            ) : (
                                                <div className="color-picker-wrapper">
                                                    <ColorPicker
                                                        color={el.colorReference}
                                                        defaultColor={'#dadada'}
                                                        onChange={color => {
                                                            barrierStatus[index].colorReference = color
                                                            setBarrierStatus([...barrierStatus])
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="color-box" style={{ backgroundColor: el.colorReference }} />
                                    )}

                                    <div className="barrier-status-custom-name-input-span">
                                        <input
                                            disabled={!editMode}
                                            type="text"
                                            id={`barrier-status-custom-name-input-${0}`}
                                            className={`barrier-status-custom-name-input ${
                                                !editMode ? 'disabled' : ''
                                            } ${
                                                isNullOrEmptyString(el.statusCustomName) && isNullOrEmptyString(el.name)
                                                    ? 'red-border'
                                                    : ''
                                            }`}
                                            placeholder={i18n('Name')}
                                            onChange={change => {
                                                barrierStatus[index].statusCustomName = change.target.value
                                                setBarrierStatus([...barrierStatus])
                                            }}
                                            value={el.statusCustomName ?? i18n(el.name)}
                                            maxLength={CUSTOM_STATUS_NAME_MAX_CHARS}
                                            onFocus={() => {
                                                const newBooleanArray = inputFocus.map((boo, idx) => {
                                                    if (idx === index) {
                                                        boo = true

                                                        return boo
                                                    }

                                                    return (boo = false)
                                                })
                                                setInputFocus(newBooleanArray)
                                            }}
                                        />
                                        {editMode &&
                                            el.statusCustomName &&
                                            el.name &&
                                            el.statusCustomName.toLowerCase() !== el.name.toLowerCase() && (
                                                <div
                                                    className="default-name-span"
                                                    style={{ marginBottom: el.priorityOrder !== 1000 && 8 }}
                                                >{`${i18n('default name:')} ${i18n(el.name)}`}</div>
                                            )}
                                    </div>
                                    {editMode && el.deleteable && (
                                        <>
                                            {el.isInUse ? (
                                                <div className="delete-icon">
                                                    <i className="far fa-trash-alt in-use" />
                                                </div>
                                            ) : (
                                                <div className="delete-icon">
                                                    <i
                                                        className="far fa-trash-alt"
                                                        onClick={() => handleRemoveStatus(index)}
                                                    />
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                                {barrierStatus.length < 11 &&
                                    editMode &&
                                    el.priorityOrder !== 1000 &&
                                    el.priorityOrder !== 2000 && (
                                        <div
                                            className={`new-status-row ${inputFocus[index] === true ? 'focus' : ''}`}
                                            onClick={() => {
                                                handleNewStatus('BOT', barrierStatus.sort(sortNumber), index)
                                            }}
                                            style={{
                                                marginTop:
                                                    el.statusCustomName &&
                                                    el.name &&
                                                    el.statusCustomName.toLowerCase() !== el.name.toLowerCase() &&
                                                    el.priorityOrder !== 1000 &&
                                                    8
                                            }}
                                        >
                                            <div className="new-status-line"></div>
                                            <div className="new-status-button">
                                                <i className="fas fa-plus" />
                                                {i18n('New Status')}
                                            </div>
                                            <div className="new-status-line"></div>
                                        </div>
                                    )}
                            </div>
                        ))}
                </div>
            </div>
            {editMode && (
                <div className={`save-bar ${editMode ? 'editing' : ''}`}>
                    <button className="save-button" onClick={handleSaveBarrierStatus}>
                        {i18n('Save')}
                    </button>
                    {i18n('or')}
                    <div className="cancel-button" onClick={handleCancel}>
                        {i18n('Cancel')}
                    </div>
                </div>
            )}
        </BarrierStatusNameStyled>
    )
}
